// Linked trades: tick several trades of a day and mark them as one trade idea, add the same tag to
// all of them, or put them all in a playbook play (a play is its setup tag). Each trade keeps its
// own tags, so one of them can still get "Early entry" on its own. Changes go to the server in one
// call and reach the rest of the page through the 'mm-trades-replaced' event (Journal.js).
import { useEffect, useMemo, useState } from 'react';
import { api } from './api';
import { useI18n } from './i18n';

export const replaceTradesEverywhere = (trades) => window.dispatchEvent(new CustomEvent('mm-trades-replaced', { detail: trades }));

// The linked groups among these trades: linkId → { n, color }.
const COLORS = ['#1fd7ef', '#b9a5fb', '#f5b941', '#2fe39a', '#ff8fa3'];
export function linkGroups(trades) {
  const counts = {};
  for (const t of trades) if (t.linkId) counts[t.linkId] = (counts[t.linkId] || 0) + 1;
  return Object.fromEntries(Object.keys(counts).map((id, i) => [id, { n: counts[id], color: COLORS[i % COLORS.length] }]));
}

export function LinkChip({ trade, groups, onPick }) {
  const { t } = useI18n();
  const g = trade.linkId && groups[trade.linkId];
  if (!g) return null;
  return (
    <span className="lt-chip" style={{ '--lt': g.color }} title={t('Linked with {n} other trades as one trade idea', { n: g.n - 1 })}
      onClick={onPick ? (e) => { e.stopPropagation(); onPick(trade.linkId); } : undefined}>
      🔗 {t('Linked · {n}', { n: g.n })}
    </span>
  );
}

let playsCache = null;
function usePlays() {
  const [plays, setPlays] = useState(playsCache || []);
  // Shows the last list right away, then refreshes it (a play may have been added since).
  useEffect(() => {
    let live = true;
    api('/playbook').then((list) => { playsCache = list || []; if (live) setPlays(playsCache); }).catch(() => {});
    return () => { live = false; };
  }, []);
  return plays;
}

// The bar shown while trades are ticked.
export function LinkBar({ trades, selected, onClear, library = [] }) {
  const { t } = useI18n();
  const plays = usePlays();
  const [tag, setTag] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const picked = useMemo(() => trades.filter((x) => selected.has(x._id)), [trades, selected]);
  const linked = picked.some((x) => x.linkId);
  const allSameGroup = picked.length > 1 && picked.every((x) => x.linkId && x.linkId === picked[0].linkId);
  if (!picked.length) return null;

  async function send(body) {
    setBusy(true);
    setError('');
    try {
      const r = await api('/trades/link', { method: 'POST', body: { ids: picked.map((x) => x._id), ...body } });
      replaceTradesEverywhere(r.trades);
      setTag('');
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }
  const tagOptions = library.filter((x) => x.kind !== 'setup').map((x) => x.name);
  return (
    <div className="lt-bar" role="region" aria-label={t('Selected trades')}>
      <b>{t(picked.length === 1 ? '{n} trade selected' : '{n} trades selected', { n: picked.length })}</b>
      {picked.length > 1 && !allSameGroup && (
        <button type="button" className="small" disabled={busy} onClick={() => send({ link: true })}>🔗 {t('Link as one trade idea')}</button>
      )}
      {linked && <button type="button" className="secondary small" disabled={busy} onClick={() => send({ link: false })}>{t('Unlink')}</button>}
      <span className="lt-field">
        <input list="lt-tags" placeholder={t('Tag all of them…')} value={tag} maxLength={40} onChange={(e) => setTag(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && tag.trim()) send({ addTags: [tag.trim()] }); }} />
        <datalist id="lt-tags">{tagOptions.map((n) => <option key={n} value={n} />)}</datalist>
        <button type="button" className="secondary small" disabled={busy || !tag.trim()} onClick={() => send({ addTags: [tag.trim()] })}>+ {t('Tag')}</button>
      </span>
      {plays.length > 0 && (
        <select className="lt-play" value="" disabled={busy} aria-label={t('Playbook')}
          onChange={(e) => { const play = plays.find((p) => p._id === e.target.value); if (play) send({ addTags: [play.tag] }); }}>
          <option value="">{t('Playbook…')}</option>
          {plays.map((p) => <option key={p._id} value={p._id}>{p.name}</option>)}
        </select>
      )}
      <button type="button" className="link-button" onClick={onClear}>{t('Clear')}</button>
      {error && <span className="error">{error}</span>}
    </div>
  );
}

// Selection state for a list of trades: tick, untick, tick a whole linked group.
export function useTradeSelection(trades) {
  const [selected, setSelected] = useState(() => new Set());
  const ids = trades.map((x) => x._id).join(',');
  useEffect(() => { setSelected(new Set()); }, [ids]);
  return {
    selected,
    toggle: (id) => setSelected((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; }),
    pickGroup: (linkId) => setSelected(new Set(trades.filter((x) => x.linkId === linkId).map((x) => x._id))),
    all: () => setSelected((s) => (s.size === trades.length ? new Set() : new Set(trades.map((x) => x._id)))),
    clear: () => setSelected(new Set()),
  };
}
